// Lib
import React, { useRef, useState } from 'react';
import { VariableSizeList as List } from 'react-window';

// Utils
import { isTitleRow } from './emojiPopupUtil';

// Hooks
import useListResizeHandlers from './hooks/useListResizeHandlers';
import useEmojiPopupData from './hooks/useEmojiPopupData';
import { UseSheetReturnType } from '../../../mobile/structural/sheet/hooks/useSheet';

// Components
import EmojiPopupRow from './emojiList/EmojiPopupRow';
import SheetSearchBar from '../../../mobile/structural/sheet/searchBar/SheetSearchBar';
import SheetContainer, {
    CloseButton,
    Content,
    Header,
    Heading,
    NonDraggableArea,
    Overlay,
    Sheet,
} from '../../../mobile/structural/sheet';
import EmojiPopupContentNoResults from './emojiList/EmojiPopupContentNoResults';
import EmojiPopupContentLoading from './emojiList/EmojiPopupContentLoading';

// Types
import { EmojisRowData } from '../emojiTypes';

// Constants
import { DEFAULT_COLUMN_COUNT, EmojiPopupContentProps } from './emojiPopupConstants';

// Styles
import './MobileEmojiSheet.scss';
import { useTranslator } from '../../i18n/useTranslator';

export const TITLE_ROW_HEIGHT = 53;
export const EMOJI_MIN_SIZE = 44;
export const COLUMN_GAP = 4;
export const EMOJI_SHEET_DEFAULT_SNAP_POINT = 0.5;

export type MobileEmojiSheetProps = EmojiPopupContentProps & {
    sheetProps: UseSheetReturnType;
    heading?: string;
};

const MobileEmojiSheet = (props: MobileEmojiSheetProps) => {
    const { gridSize, sheetProps, selectedEmojiIds, onSelectEmoji, closePopup } = props;

    const translate = useTranslator('popup', 'emoji.sheet');

    const heightControllerRef = useRef<HTMLDivElement>(null);

    const [columnCount, setColumnCount] = useState(DEFAULT_COLUMN_COUNT);
    const [emojiButtonSize, setEmojiButtonSize] = useState(EMOJI_MIN_SIZE);

    const getRowHeight = (index: number, emojisRowData: EmojisRowData) =>
        isTitleRow(emojisRowData[index]) ? TITLE_ROW_HEIGHT : emojiButtonSize + COLUMN_GAP;

    const {
        emojiDataLoaded,
        emojisRowData,
        handleEmojiListScroll,
        searchFilter,
        setSearchFilter,
        listRef,
        getKeyOfItemAtIndex,
        getHeightOfItemAtIndex,
        handleClickEmoji,
        skinTone,
    } = useEmojiPopupData(onSelectEmoji, closePopup, getRowHeight, columnCount);

    const { listHeight, listWidth } = useListResizeHandlers(
        listRef,
        heightControllerRef,
        setColumnCount,
        setEmojiButtonSize,
        sheetProps.isSheetOpen,
    );

    const scrollToTop = () => {
        listRef.current && listRef.current.scrollTo(0);
    };

    return (
        <SheetContainer
            className="emoji-tool"
            {...sheetProps}
            defaultSnapPoint={EMOJI_SHEET_DEFAULT_SNAP_POINT}
            snapPoints={[0.5, 0.9]}
        >
            <Overlay />
            <Sheet>
                <Header>
                    <Heading>
                        {translate('heading', 'Add reaction')}
                    </Heading>
                    <CloseButton />
                </Header>
                <SheetSearchBar searchFilter={searchFilter} setSearchFilter={setSearchFilter} onChange={scrollToTop} />
                <Content>
                    <NonDraggableArea>
                        <div className="height-controller" ref={heightControllerRef}>
                            {emojiDataLoaded && emojisRowData.length ? (
                                <List
                                    className="emoji-list"
                                    onScroll={handleEmojiListScroll}
                                    ref={listRef}
                                    width={listWidth}
                                    height={listHeight}
                                    itemCount={emojisRowData.length}
                                    itemSize={getHeightOfItemAtIndex}
                                    itemKey={getKeyOfItemAtIndex}
                                    itemData={{
                                        rows: emojisRowData,
                                        skinTone,
                                        selectedEmojiIds,
                                        handleClickEmoji,
                                        gridSize,
                                        emojiButtonSize,
                                    }}
                                    overscanCount={10}
                                >
                                    {EmojiPopupRow}
                                </List>
                            ) : emojiDataLoaded ? (
                                <EmojiPopupContentNoResults
                                    style={{ width: listWidth, height: listHeight }}
                                    gridSize={gridSize}
                                />
                            ) : (
                                <EmojiPopupContentLoading style={{ width: listWidth, height: listHeight }} />
                            )}
                        </div>
                    </NonDraggableArea>
                </Content>
            </Sheet>
        </SheetContainer>
    );
};

export default MobileEmojiSheet;
